<script setup lang="ts">
import { isNil, isNumber } from 'lodash';
import { computed } from 'vue';

type KeyValueListVariant = 'list' | 'list-stacked' | 'list-inline' | 'grid';
type KeyValueListSize = 'regular' | 'small';

const {
  variant = 'list',
  size = 'regular',
  isDivided = false,
  labelWidth = null
} = defineProps<{
  variant?: KeyValueListVariant;
  size?: KeyValueListSize;
  isDivided?: boolean;
  labelWidth?: string | number | null;
}>();

const keyValueClasses = computed(() => ({
  '-list-stacked': variant === 'list' || variant === 'list-stacked',
  '-list-inline': variant === 'list-inline',
  '-grid': variant === 'grid',
  '-divided': isDivided,
  '-size-regular': size === 'regular',
  '-size-small': size === 'small'
}));

const keyValueStyles = computed(() => {
  if (isNil(labelWidth) || labelWidth === '') {
    return {};
  }

  return {
    '--key-value-label-width': isNumber(labelWidth) ? `${labelWidth}px` : labelWidth
  };
});
</script>

<template>
  <dl class="key-value-list" :class="keyValueClasses" :style="keyValueStyles">
    <slot></slot>
  </dl>
</template>

<style scoped lang="scss">
.key-value-list {
  :deep(.key-value) {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 0;

    dt {
      @include typography('body-sm-bold');
      font-size: var(--key-value-label-font-size) !important;
    }

    dd {
      @include typography('body-md');
      font-size: var(--key-value-value-font-size) !important;
    }
  }
}

.-list-stacked {
  display: flex;
  flex-direction: column;
  gap: $spacing-4;
}

.-list-inline {
  display: flex;
  flex-direction: column;
  gap: $spacing-2;
}

/**
  * Divided styles: Apply ONLY when divided is used alongside list variants.
  */
.-divided.-list-stacked,
.-divided.-list-inline {
  > :not(:last-child, :only-child) {
    border-bottom: 1px solid $color-line-border;
    padding-bottom: $spacing-3;
  }
}

.-list-inline {
  :deep(.key-value) {
    flex-flow: row wrap;
    align-items: center;
    gap: $spacing-2;
    padding: $spacing-1 0;
  }

  :deep(.key-value dt) {
    width: var(--key-value-label-width, auto);
    flex: 0 0 var(--key-value-label-width, auto);
  }

  :deep(.key-value dd) {
    flex: 1 1 auto;
    min-width: 0;
  }
}

.-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr); // keeping this as a fallback for now
  grid-template-columns: repeat(auto-fit, minmax(30%, 150px));
  gap: $spacing-3;
  width: 100%;

  :deep(.key-value) {
    padding: $spacing-1 0;
  }
}

.-size-small {
  --key-value-label-font-size: 12px; /* check with design */
  --key-value-value-font-size: 14px; /* check with design */
}

.-size-regular {
  --key-value-label-font-size: 14px; /* check with design */
  --key-value-value-font-size: 16px; /* check with design */
}
</style>
